'use client';

/**
 * QuantityChangeGiftSelectorWidget
 *
 * Bottom sheet component for re-selecting all gifts after quantity change.
 * This is used when user changes the quantity of a cart item.
 *
 * Key features:
 * - Shows a quantity stepper to change the product quantity
 * - Shows all available gift rules for the product
 * - Allows user to re-select gifts from each rule
 * - Updates the cart item reference with new quantity and gift selections
 *
 * Uses real withAppBottomSheet HOC from mario-nextjs-react-ui-core
 * (responsive: Modal on desktop, BottomSheet on mobile viewport)
 */
import {
    withAppBottomSheet,
    type AppBottomSheetHeaderProps,
    type AppBottomSheetExtraParamsProps,
    type AppBottomSheetBackdropProps,
    type AppBottomSheetFooterProps,
} from '@kidslabs/mario-nextjs-react-ui-core';
import React, {
    useCallback,
    useImperativeHandle,
    useMemo,
    forwardRef,
    useState,
    type ForwardRefRenderFunction,
    type ForwardedRef,
} from 'react';
import cssStyles from './quantity-change-gift-selector-widget.module.css';
import {
    type QuantityChangeGiftSelectorWidgetProps,
    type QuantityChangeGiftSelectorWidgetRef,
} from './quantity-change-gift-selector-widget.types';
import { GiftsSelectorWidgetContents } from '../gifts-selector-widget-contents/gifts-selector-widget-contents/gifts-selector-widget-contents';
import { GiftSelectorBottomSheetFooter } from '../gift-selector-bottom-sheet-footer/gift-selector-bottom-sheet-footer';
import { useProductGiftHook } from '../../hooks/product-gift.hook';
import { useCartItemGiftSelection } from '../../hooks/use-cart-item-gift-selection.hook';
import type { CartItemReference as QuoteCartItemReference } from '@kidslabs/module-quote';
import { CartCounterButtonWidget } from '../cart-counter-button/cart-counter-button-widget';
import { CartCounterButtonEnum } from '../../../../enums';

// Memoized footer component to prevent unnecessary re-renders
const MemoizedFooter = React.memo<{
    onBottomSheetClose: () => void;
    onConfirm: () => void;
}>(({ onBottomSheetClose, onConfirm }) => {
    return <GiftSelectorBottomSheetFooter onBottomSheetClose={onBottomSheetClose} onConfirm={onConfirm} />;
});

MemoizedFooter.displayName = 'MemoizedFooter';

// Memoized quantity stepper header using CartCounterButtonWidget
const QuantityStepperHeader = React.memo<{
    currentQuantity: number;
    onIncrease: (value: number) => void;
    onDecrease: (value: number) => void;
    onValueChange: (value: number) => void;
}>(({ currentQuantity, onIncrease, onDecrease, onValueChange }) => {
    return (
        <div className={cssStyles.quantityStepperContainer}>
            <CartCounterButtonWidget
                value={currentQuantity}
                handleIncrease={onIncrease}
                handleDecrease={onDecrease}
                emitValueChange={onValueChange}
                variant={CartCounterButtonEnum.COUNTER}
            />
        </div>
    );
});

QuantityStepperHeader.displayName = 'QuantityStepperHeader';

// Memoized content component to prevent unnecessary re-renders
const MemoizedContent = React.memo<{
    product: QuantityChangeGiftSelectorWidgetProps['product'];
    productQuantity: number;
    cartItemReference: QuantityChangeGiftSelectorWidgetProps['cartItemReference'];
    onIncrease: (value: number) => void;
    onDecrease: (value: number) => void;
    onValueChange: (value: number) => void;
    enableAutoFill?: boolean;
    onAutoFillQuantity?: (newQuantity: number) => void;
}>(
    ({
        product,
        productQuantity,
        cartItemReference,
        onIncrease,
        onDecrease,
        onValueChange,
        enableAutoFill,
        onAutoFillQuantity,
    }) => {
        return (
            <div className={cssStyles.bottomSheetInjectContent}>
                <QuantityStepperHeader
                    currentQuantity={productQuantity}
                    onIncrease={onIncrease}
                    onDecrease={onDecrease}
                    onValueChange={onValueChange}
                />
                <GiftsSelectorWidgetContents
                    product={product}
                    productQuantity={productQuantity}
                    cartItemReference={cartItemReference}
                    enableAutoFill={enableAutoFill}
                    onAutoFillQuantity={onAutoFillQuantity}
                />
            </div>
        );
    }
);

MemoizedContent.displayName = 'MemoizedContent';

const QuantityChangeGiftSelectorWidgetInner: ForwardRefRenderFunction<
    QuantityChangeGiftSelectorWidgetRef,
    QuantityChangeGiftSelectorWidgetProps
> = (props: QuantityChangeGiftSelectorWidgetProps, ref: ForwardedRef<QuantityChangeGiftSelectorWidgetRef>) => {
    const {
        bottomSheet,
        product,
        initialQuantity,
        cartItemReference,
        onClose,
        onQuantityChange: onQuantityChangeProp,
        onConfirm: onConfirmInput,
    } = props;

    // Local state for quantity (initialized from initialQuantity)
    const [currentQuantity, setCurrentQuantity] = useState(initialQuantity);

    const { sortedGiftsDataList, addGiftToCartItemReferenceLogic } = useProductGiftHook({
        product: product as { sku?: string | null; id?: number | null; name?: string | null },
        fetchGiftsFromCache: false,
    });

    const logics = cartItemReference?.logics ?? [];
    const sessionId = logics?.[0]?.sessionId;

    // Use cart item gift selection hook
    const cartGiftSelection = useCartItemGiftSelection({
        cartItemReference: {
            ...cartItemReference,
            qty: currentQuantity, // Use updated quantity
        } as unknown as QuoteCartItemReference,
        availableGifts: sortedGiftsDataList,
        sessionId: sessionId ?? '',
    });

    // Handle quantity increase (CounterButton passes value delta)
    const handleIncrease = useCallback(
        (value: number = 1) => {
            const newQuantity = currentQuantity + value;
            setCurrentQuantity(newQuantity);
            onQuantityChangeProp?.(newQuantity);
        },
        [currentQuantity, onQuantityChangeProp]
    );

    // Handle quantity decrease (CounterButton passes value delta)
    const handleDecrease = useCallback(
        (value: number = 1) => {
            if (currentQuantity > 1) {
                const newQuantity = Math.max(1, currentQuantity - value);
                setCurrentQuantity(newQuantity);
                onQuantityChangeProp?.(newQuantity);
            }
        },
        [currentQuantity, onQuantityChangeProp]
    );

    // Handle direct value change from CounterButton
    const handleValueChange = useCallback(
        (value: number) => {
            const newQuantity = Math.max(1, value);
            setCurrentQuantity(newQuantity);
            onQuantityChangeProp?.(newQuantity);
        },
        [onQuantityChangeProp]
    );

    // Handle auto-fill: set quantity to required amount
    const handleAutoFillQuantity = useCallback(
        (newQuantity: number) => {
            setCurrentQuantity(newQuantity);
            onQuantityChangeProp?.(newQuantity);
        },
        [onQuantityChangeProp]
    );

    // Memoize the selected gifts observable reference
    const selectedGiftsObservable = useMemo(() => cartGiftSelection.store$?.selectedGifts, [cartGiftSelection.store$]);

    // Memoize close handler
    const handleBottomSheetClose = useCallback(() => {
        bottomSheet?.close();
        onClose?.();
    }, [bottomSheet, onClose]);

    // Memoize confirm handler
    const handleConfirm = useCallback(() => {
        addGiftToCartItemReferenceLogic({ selectedGifts$: selectedGiftsObservable, sessionId: sessionId ?? '' });
        onConfirmInput?.();
        bottomSheet?.close();
    }, [addGiftToCartItemReferenceLogic, bottomSheet, onConfirmInput, selectedGiftsObservable, sessionId]);

    // Header configuration for bottom sheet
    const headerProps = useMemo<AppBottomSheetHeaderProps>(
        () => ({
            title: 'Chọn quà tặng',
            titleProps: { variant: 'h2', weight: 'bold', align: 'center' },
            closeIconProps: {
                icon: 'blank',
                disabled: true,
                iconColor: 'transparent',
            },
            onClose: handleBottomSheetClose,
        }),
        [handleBottomSheetClose]
    );

    // Memoize extra params
    const extraParams = useMemo<AppBottomSheetExtraParamsProps>(
        () => ({
            enableDynamicSizing: false,
            scrollEnabled: false,
            enableContentPanningGesture: false,
            snapPoints: ['85%', '85%'],
        }),
        []
    );

    // Memoize footer
    const footerElement = useMemo<AppBottomSheetFooterProps>(
        () => ({ component: <MemoizedFooter onBottomSheetClose={handleBottomSheetClose} onConfirm={handleConfirm} /> }),
        [handleBottomSheetClose, handleConfirm]
    );

    // Memoize backdrop props
    const backdropProps = useMemo<AppBottomSheetBackdropProps>(
        () => ({
            pressBehavior: 'close',
        }),
        []
    );

    // Memoize open function
    const openBottomSheet = useCallback(() => {
        // Reset quantity to initial value when opening
        setCurrentQuantity(initialQuantity);

        // ✅ Build fresh content with CURRENT data immediately
        const freshContent = (
            <div className={cssStyles.bottomSheetInjectContent}>
                <QuantityStepperHeader
                    currentQuantity={initialQuantity}
                    onIncrease={handleIncrease}
                    onDecrease={handleDecrease}
                    onValueChange={handleValueChange}
                />
                <GiftsSelectorWidgetContents
                    product={product}
                    productQuantity={initialQuantity}
                    cartItemReference={cartItemReference}
                    enableAutoFill={true}
                    onAutoFillQuantity={handleAutoFillQuantity}
                />
            </div>
        );

        // Inject content into bottom sheet using real UI Core HOC
        bottomSheet?.inject(freshContent, headerProps, extraParams, footerElement, backdropProps);
        bottomSheet?.show();
    }, [
        initialQuantity,
        cartItemReference,
        product,
        footerElement,
        bottomSheet,
        headerProps,
        extraParams,
        backdropProps,
        handleIncrease,
        handleDecrease,
        handleValueChange,
        handleAutoFillQuantity,
    ]);

    // Expose API to parent via ref
    useImperativeHandle(
        ref,
        () => ({
            openBottomSheet,
        }),
        [openBottomSheet]
    );

    return null;
};

QuantityChangeGiftSelectorWidgetInner.displayName = 'QuantityChangeGiftSelectorWidgetInner';

// Export component with forwardRef and HOC
export const QuantityChangeGiftSelectorWidget = withAppBottomSheet(forwardRef(QuantityChangeGiftSelectorWidgetInner));
